// File: src/services/FamilyRepository.ts

import { firebaseDB } from "../firebase/firebase";
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";

import { AppUserId } from "../../domain/types/AppUser";
import { Family, FamilyId } from "../../domain/types/Family";
import { ProviderUser } from "../../domain/types/ProviderUser";
import { FirestoreAppUserRepository } from "./appUserRepository";
import { findByProviderUserId } from "./providerUserRepository";

export class FirestoreFamilyRepository {
  private familiesCollection = collection(firebaseDB, "families");

  async create(family: Family): Promise<void> {
    const familyRef = doc(this.familiesCollection, family.familyId);
    console.log("Creating family:", family);
    await setDoc(familyRef, family);
  }

  async findByFamilyId(familyId: FamilyId): Promise<Family | null> {
    const familyDoc = await getDoc(doc(this.familiesCollection, familyId));
    return familyDoc.exists() ? (familyDoc.data() as Family) : null;
  }

  async findFamilyByAppUserId(appUserId: AppUserId): Promise<Family | null> {
    const appUserRepository = new FirestoreAppUserRepository();
    const appUser = await appUserRepository.findByAppUserId(appUserId);

    if (appUser && appUser.familyId) {
      return await this.findByFamilyId(appUser.familyId);
    } else {
      return null;
    }
  }

  async addMemberToFamily(familyId: string, userId: string): Promise<void> {
    const familyRef = doc(firebaseDB, "families", familyId);
    await updateDoc(familyRef, {
      members: arrayUnion(userId),
    });
  }

  async updateAdmin(
    familyId: FamilyId,
    newAdmin: AppUserId,
  ): Promise<Family | null> {
    const familyRef = doc(this.familiesCollection, familyId);
    const familyDoc = await getDoc(familyRef);

    if (familyDoc.exists()) {
      await updateDoc(familyRef, { admin: newAdmin });
      const updatedFamilyDoc = await getDoc(familyRef);
      return updatedFamilyDoc.data() as Family;
    }

    return null;
  }

  async getFamilyMembers(
    familyId: FamilyId,
  ): Promise<Record<AppUserId, ProviderUser>> {
    const familyDoc = await getDoc(doc(this.familiesCollection, familyId));
    const memberAppUserIds: AppUserId[] = familyDoc.exists()
      ? familyDoc.data()?.members
      : [];

    const appUserRepository = new FirestoreAppUserRepository();

    const providerUsersMap: Record<AppUserId, ProviderUser> = {};

    for (const appUserId of memberAppUserIds) {
      const appUser = await appUserRepository.findByAppUserId(appUserId);
      if (appUser && appUser.primaryProviderUserId) {
        const primaryProviderUserId = appUser.primaryProviderUserId;

        const primaryProviderUser = await findByProviderUserId(
          primaryProviderUserId,
        );

        if (primaryProviderUser) {
          providerUsersMap[appUserId] = primaryProviderUser;
        } else {
          console.warn(
            `No ProviderUser found for primaryProviderUserId: ${primaryProviderUserId} of appUserId: ${appUserId}`,
          );
        }
      } else {
        console.warn(
          `AppUser ${appUserId} does not have a primaryProviderUserId`,
        );
      }
    }

    console.debug("getFamilyMembers: providerUsersMap:", providerUsersMap);

    return providerUsersMap;
  }
}
